import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { apiGet } from '../../api/client';
import { STATUS_CLASS, STATUS_LABEL, type Receipt } from '../../types';
import { fmtDate, fmtTL } from '../../format';

const MONTHS = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

function formatMonth(ym: string) {
  const [y, m] = ym.split('-');
  return `${MONTHS[parseInt(m, 10) - 1]} ${y}`;
}

export default function EmployeeReceipts() {
  const [items, setItems] = useState<Receipt[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [activeMonth, setActiveMonth] = useState<string | null>(null);

  useEffect(() => {
    apiGet<{ receipts: Receipt[] }>('/api/receipts')
      .then((d) => setItems(d.receipts))
      .catch((e: Error) => setErr(e.message));
  }, []);

  const grouped = useMemo(() => {
    if (!items) return {};
    const acc: Record<string, Receipt[]> = {};
    for (const r of items) {
      const ym = r.receipt_date.substring(0, 7);
      if (!acc[ym]) acc[ym] = [];
      acc[ym].push(r);
    }
    return acc;
  }, [items]);

  const monthKeys = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

  if (activeMonth && grouped[activeMonth]) {
    const monthItems = grouped[activeMonth];
    return (
      <div>
        <div className="mb-4">
          <button onClick={() => setActiveMonth(null)} className="text-sm text-brand-600 hover:underline font-medium">
            ← Klasörlere Dön
          </button>
        </div>
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold">{formatMonth(activeMonth)} Fişleri</h1>
          <Link to="/fisler/yeni" className="btn-primary">+ Yeni Fiş</Link>
        </div>
        <div className="card overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-2">Tarih</th>
                <th className="text-left px-4 py-2">Tür</th>
                <th className="text-left px-4 py-2">Şirket</th>
                <th className="text-right px-4 py-2">Tutar (Net)</th>
                <th className="text-right px-4 py-2">KDV</th>
                <th className="text-right px-4 py-2">Toplam</th>
                <th className="text-left px-4 py-2">Durum</th>
                <th className="text-left px-4 py-2">Not</th>
                <th className="text-left px-4 py-2">Yönetici Notu</th>
              </tr>
            </thead>
            <tbody>
              {monthItems.map((r) => (
                <tr key={r.id} className="border-t border-slate-100">
                  <td className="px-4 py-2">{fmtDate(r.receipt_date)}</td>
                  <td className="px-4 py-2">{r.receipt_type}</td>
                  <td className="px-4 py-2">{r.company_name}</td>
                  <td className="px-4 py-2 text-right">{fmtTL(r.amount_net)}</td>
                  <td className="px-4 py-2 text-right">%{r.vat_rate}</td>
                  <td className="px-4 py-2 text-right font-semibold">{fmtTL(r.total_gross)}</td>
                  <td className="px-4 py-2">
                    <span className={STATUS_CLASS[r.status]}>{STATUS_LABEL[r.status]}</span>
                  </td>
                  <td className="px-4 py-2 text-slate-500 max-w-[150px] truncate" title={r.employee_note || ''}>
                    {r.employee_note ?? '—'}
                  </td>
                  <td className="px-4 py-2 text-slate-600">{r.admin_note ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Fişlerim (Dönemler)</h1>
        <Link to="/fisler/yeni" className="btn-primary">+ Yeni Fiş</Link>
      </div>
      {err && <div className="text-red-600 mb-4">{err}</div>}
      {items === null ? (
        <div className="text-slate-500">Yükleniyor…</div>
      ) : items.length === 0 ? (
        <div className="card p-8 text-center text-slate-500">
          Henüz fiş göndermediniz. <Link to="/fisler/yeni" className="text-brand-600">İlk fişi ekleyin</Link>.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {monthKeys.map((ym) => {
            const count = grouped[ym].length;
            const pending = grouped[ym].filter(x => x.status === 'pending').length;
            return (
              <button
                key={ym}
                onClick={() => setActiveMonth(ym)}
                className="card p-6 flex flex-col items-start hover:shadow-md transition-shadow text-left border border-slate-200 hover:border-brand-400 cursor-pointer"
              >
                <div className="text-4xl mb-3">📁</div>
                <h3 className="text-lg font-bold text-slate-800">{formatMonth(ym)}</h3>
                <div className="text-sm text-slate-500 mt-1">
                  Toplam {count} fiş
                  {pending > 0 && <span className="ml-2 text-amber-600 font-medium badge-pending">({pending} bekleyen)</span>}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
